import { createSignal, createEffect } from 'solid-js';

// TimeInput24 — a 24-hour time field that is GUARANTEED to show HH:MM with no
// AM/PM on every device. The native <input type="time"> renders 12h/24h
// according to the OS/region (iOS Safari ignores the page lang entirely), so a
// fully-controlled text field is the only way to force 24-hour everywhere.
//
// Props: value ("HH:MM" or ""), onChange(val), disabled, required, class,
// placeholder. Numeric entry is masked live ("1600" → "16:00") and validated
// on blur (out-of-range reverts to the last good value; empty is allowed).
// Letters are stripped on input, so a value like "AA:BB" can never be entered.
// Arrow keys step the part the caret is in: ↑/↓ on the hour ±1 (wraps 0–23),
// on the minutes snaps ±15 (00 → 15 → 30 → 45 → 00).

// maskTime formats the digits typed so far as a partial HH:MM (no validation).
function maskTime(s) {
  const d = s.replace(/\D/g, '').slice(0, 4);
  if (d.length <= 2) return d;
  if (d.length === 3) return d[0] + ':' + d.slice(1); // "930" → "9:30"
  return d.slice(0, 2) + ':' + d.slice(2);            // "1600" → "16:00"
}

// normalizeTime returns canonical "HH:MM", "" for empty, or null if invalid.
function normalizeTime(s) {
  const t = (s || '').trim();
  if (t === '') return '';
  const d = t.replace(/\D/g, '');
  if (d.length === 0) return null;
  let hh, mm;
  if (d.length <= 2) { hh = +d; mm = 0; }
  else if (d.length === 3) { hh = +d[0]; mm = +d.slice(1); }
  else { hh = +d.slice(0, 2); mm = +d.slice(2, 4); }
  if (hh > 23 || mm > 59) return null;
  return String(hh).padStart(2, '0') + ':' + String(mm).padStart(2, '0');
}

const pad = (n) => String(n).padStart(2, '0');
// stepMinute snaps to the next/previous quarter-hour, wrapping 0..45.
function stepMinute(mm, dir) {
  if (dir > 0) return (Math.floor(mm / 15) * 15 + 15) % 60;
  return ((Math.ceil(mm / 15) * 15 - 15) % 60 + 60) % 60;
}

export default function TimeInput24(props) {
  const [text, setText] = createSignal(props.value || '');
  // Resync when the value changes externally (e.g. a refetch or a reset).
  createEffect(() => setText(props.value || ''));

  function onInput(e) {
    const masked = maskTime(e.currentTarget.value);
    // Force the DOM to the masked value even when the signal is unchanged
    // (e.g. typing a letter into an empty field masks to "" == "", and Solid
    // would otherwise leave the stray character on screen).
    e.currentTarget.value = masked;
    setText(masked);
  }
  function commit() {
    const n = normalizeTime(text());
    if (n === null) { setText(props.value || ''); return; } // revert invalid
    setText(n);
    if (n !== (props.value || '')) props.onChange?.(n);
  }
  function onKeyDown(e) {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const el = e.currentTarget;
      const cur = normalizeTime(text());
      let hh = cur ? +cur.slice(0, 2) : 0;
      let mm = cur ? +cur.slice(3, 5) : 0;
      // "HH:MM": caret 0–2 → hour, 3+ → minute.
      const inMinute = (el.selectionStart ?? 5) >= 3;
      const dir = e.key === 'ArrowUp' ? 1 : -1;
      if (inMinute) mm = stepMinute(mm, dir);
      else hh = (hh + dir + 24) % 24;
      const v = pad(hh) + ':' + pad(mm);
      setText(v);
      props.onChange?.(v);
      // Re-select the stepped part so repeated presses keep working on it.
      requestAnimationFrame(() => {
        try { el.setSelectionRange(inMinute ? 3 : 0, inMinute ? 5 : 2); } catch (_) {}
      });
      return;
    }
    if (e.key === 'Enter') e.currentTarget.blur();
  }

  return (
    <input
      type="text"
      inputmode="numeric"
      autocomplete="off"
      class={`tinput24 ${props.class || ''}`}
      placeholder={props.placeholder ?? 'HH:MM'}
      maxlength="5"
      value={text()}
      disabled={props.disabled}
      required={props.required}
      onInput={onInput}
      onKeyDown={onKeyDown}
      onBlur={commit}
    />
  );
}
